import { useCallback, useEffect, useState } from 'react'
import { functions } from '../../config/firebase'
import getDoc from '../../utils/db/getDoc'
import getCollection from '../../utils/db/getCollection'
import {
  getCallableErrorMessage,
  normalizeSyncDatesDoc,
  parseLastSyncFromResponse,
} from './syncDateUtils'

const loadSyncDates = () =>
  getDoc({ path: 'api', docId: 'requestsDates' }).then((dates) => normalizeSyncDatesDoc(dates))

const buildSyncSummary = (item, data) => {
  if (!data || typeof data !== 'object') {
    return null
  }

  if (item.id === 'remains' && data.feeds) {
    const premium = data.feeds.premium === 'ok' ? 'Premium ✓' : 'Premium ✗'
    const outlet = data.feeds.outlet === 'ok' ? 'Outlet ✓' : 'Outlet ✗'
    return `Фіди: ${premium}, ${outlet}`
  }

  if (item.id === 'outlet') {
    const written = data.writtenOutletProductsCount
    if (typeof written === 'number') {
      return `Оновлено: ${written}`
    }
  }

  if (item.id === 'siteSearch') {
    if (data.dateSynced === false) {
      return 'Файл ок, дата ні'
    }
    if (data.counts) {
      return `P${data.counts.products || 0}/O${data.counts.outlet || 0}`
    }
  }

  if (item.id === 'premium' && Array.isArray(data.newProducts)) {
    const created = data.newProducts.filter(Boolean).length
    const updated = data.updatedProducts?.filter(Boolean).length ?? 0
    return `+${created} / ~${updated}`
  }

  return null
}

export const useProductsSync = () => {
  const [syncDates, setSyncDates] = useState({})
  const [loadingId, setLoadingId] = useState(null)
  const [itemResults, setItemResults] = useState({})

  const reloadSyncDates = useCallback(async () => {
    const dates = await loadSyncDates()
    setSyncDates(dates)
    return dates
  }, [])

  useEffect(() => {
    reloadSyncDates()
  }, [reloadSyncDates])

  const runSync = useCallback(
    async (item) => {
      if (item.isPlaceholder || item.disabled || !item.name) {
        return { ok: false }
      }

      setLoadingId(item.id)
      setItemResults((prev) => ({ ...prev, [item.id]: null }))

      try {
        const callableOptions =
          item.name === 'syncRemains' ||
          item.name === 'syncOutletProducts' ||
          item.name === 'rebuildSiteSearchIndex'
            ? { timeout: 540000 }
            : undefined
        const callable = functions.httpsCallable(item.name, callableOptions)

        if (item.syncAllBrands) {
          const brands = await getCollection({ path: 'brands', docIdName: 'id' })
          let successCount = 0
          let lastError = null

          for (const brand of brands) {
            try {
              await callable({ brandId: brand.id })
              successCount += 1
            } catch (error) {
              lastError = error
              console.error(`Sync failed for brand ${brand.id}:`, error)
            }
          }

          if (successCount === 0) {
            throw lastError || new Error('Не вдалося синхронізувати жоден бренд')
          }

          await reloadSyncDates()

          const result = {
            ok: true,
            summary: `${successCount} з ${brands.length} брендів`,
          }
          setItemResults((prev) => ({ ...prev, [item.id]: result }))
          return result
        }

        const resp = await callable(item.payload ?? null)
        const parsedDate = parseLastSyncFromResponse(resp.data)

        if (parsedDate && item.dateKey) {
          setSyncDates((prev) => ({
            ...prev,
            [item.dateKey]: parsedDate,
          }))
        }

        await reloadSyncDates()

        const result = {
          ok: true,
          summary: buildSyncSummary(item, resp.data),
        }
        setItemResults((prev) => ({ ...prev, [item.id]: result }))
        return result
      } catch (error) {
        console.error(`Sync failed: ${item.name}`, error)
        const result = {
          ok: false,
          message: getCallableErrorMessage(error),
        }
        setItemResults((prev) => ({ ...prev, [item.id]: result }))
        return result
      } finally {
        setLoadingId(null)
      }
    },
    [reloadSyncDates]
  )

  return {
    syncDates,
    loadingId,
    itemResults,
    runSync,
  }
}
